import * as THREE from 'three';
import type { GLTFLoaderPlugin, GLTFParser } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { importNavMesh, init } from '@recast-navigation/core';
import { Course, Hole } from '../../objects';

export const EXTENSION_NAME = 'SLOPE_Metadata';

const moduleInitiliazed = init();

/**
 * Parses a location string in "latitude,longitude" format into a THREE.Vector2 Longitude/Latitude pair X/Y.
 */
const parseLocation = (location: string): THREE.Vector2 => {
  const [lat, lon] = location.split(',').map((coord) => parseFloat(coord.trim()));
  return new THREE.Vector2(lon, lat);
};

export interface SLOPEMetadataDef {
  type: 'Course' | 'Hole';
  location: string;
  navMesh: {
    bufferView: number;
  };
}

export class SLOPEMetadata implements GLTFLoaderPlugin {
  parser: GLTFParser;

  constructor(parser: GLTFParser) {
    this.parser = parser;
  }

  /** Must match the extension name you wrote into the glTF JSON. */
  get name(): string {
    return EXTENSION_NAME;
  }

  /** Called after the entire scene has been parsed. */
  loadNode(nodeIndex: number): Promise<THREE.Object3D> | null {
    const { json } = this.parser;
    const nodes = json.nodes || [];

    const nodeDef = nodes[nodeIndex];
    const ext = nodeDef.extensions?.[EXTENSION_NAME] as SLOPEMetadataDef;
    if (!ext) return null;

    if (ext.location === undefined) {
      console.warn(
        `SLOPEMetadata: Node ${nodeIndex} is missing required "location" property in SLOPE_Metadata extension.`,
      );
      return this.parser.loadNode(nodeIndex);
    }

    if (ext.navMesh === undefined || typeof ext.navMesh.bufferView !== 'number') {
      console.warn(
        `SLOPEMetadata: Node ${nodeIndex} is missing required "navMesh" property in SLOPE_Metadata extension.`,
      );
      return this.parser.loadNode(nodeIndex);
    }

    const accessorIndex = ext.navMesh.bufferView;
    const nodeType = ext.type;
    const nodeLocation = parseLocation(ext.location);
    const navMeshData = this.parser.getDependency(
      'accessor',
      accessorIndex,
    ) as Promise<THREE.BufferAttribute | null>;
    const node = this.parser.loadNode(nodeIndex);

    return Promise.all([navMeshData, node]).then(async ([navMeshData, node]) => {
      await moduleInitiliazed;
      if (!navMeshData) return node;

      const { navMesh } = importNavMesh(new Uint8Array(navMeshData.array));
      const object =
        nodeType === 'Hole' ? new Hole(nodeLocation, navMesh) : new Course(nodeLocation, navMesh);
      node.copy.call(object, node, true);
      return object as THREE.Object3D;
    });
  }
}

export default SLOPEMetadata;
